import axios from 'axios';
import * as React from 'react';
import {
  createContext,
  useContext,
  useEffect,
  useState,
  useReducer,
} from 'react';
import { useMutation } from '@tanstack/react-query';
import { useUser } from './UserContext';

export interface IProductListContext {
  clearFlavoursToAdd: () => void;
  flavoursToAdd: FlavourToAdd[];
  isPending: boolean;
  isSuccess: boolean;
  addFlavour: (data: FlavourToAdd) => void;
  removeFlavour: (flavourId: number) => void;
  getUnits: (flavourIds?: number[]) => number;
  getTotal: (flavourIds?: number[]) => number;
  addToBasket: () => void;
}
export interface FlavourToAdd {
  flavourId: number;
  quantity: number;
  price: number;
}

export const flavoursReducer = (
  flavoursToAdd: FlavourToAdd[],
  action: { type: 'ADD' | 'REMOVE' | 'CLEAR'; payload?: FlavourToAdd }
) => {
  switch (action.type) {
    case 'ADD': {
      if (
        !flavoursToAdd.find(
          ({ flavourId }) => flavourId === action?.payload?.flavourId
        )
      ) {
        let newFlavoursToAdd = [...flavoursToAdd];
        if (action?.payload) {
          newFlavoursToAdd.push(action.payload);
        }
        return newFlavoursToAdd;
      } else {
        return flavoursToAdd.map((flavour) =>
          flavour.flavourId === action?.payload?.flavourId
            ? { ...flavour, ...action?.payload }
            : flavour
        );
      }
    }

    case 'REMOVE':
      return flavoursToAdd.filter(
        (el) => el.flavourId !== action?.payload?.flavourId
      );
    case 'CLEAR':
      return [];
    default:
      return flavoursToAdd;
  }
};

export const ProductListContext = createContext<IProductListContext>({
  clearFlavoursToAdd: () => {},
  flavoursToAdd: [],
  isPending: false,
  isSuccess: false,
  // @ts-ignore
  addFlavour: null,
  // @ts-ignore
  removeFlavour: null,
  // @ts-ignore
  getUnits: null,
  // @ts-ignore
  getTotal: null,
  // @ts-ignore
  addToBasket: null,
});

export function ProductListProvider({ children }: any) {
  const { fetchUser } = useUser();
  const {
    mutate,
    isPending,
    isSuccess,
    reset: resetMutation,
  } = useMutation({
    mutationFn: ({
      flavourId,
      quantity,
    }: {
      flavourId: number;
      quantity: number;
    }) =>
      axios.post(`/api/onlineshop/basket-positions/${flavourId}/${quantity}`),
  });
  const [flavoursToAdd, dispatch] = useReducer(flavoursReducer, []);

  const addFlavour = (data: FlavourToAdd) => {
    dispatch({ type: 'ADD', payload: data });
  };

  const removeFlavour = (flavourId: number) => {
    dispatch({
      type: 'REMOVE',
      payload: { flavourId: flavourId, quantity: 0, price: 0 },
    });
  };

  const getUnits = (flavourIds?: number[]) => {
    let count = 0;
    flavoursToAdd.forEach(({ flavourId, quantity }) => {
      if (flavourIds && !flavourIds.some((id) => id === flavourId)) {
        return;
      }
      count += quantity;
    });
    return count;
  };

  const getTotal = (flavourIds?: number[]) => {
    let count = 0;
    flavoursToAdd.forEach(({ flavourId, price, quantity }) => {
      if (flavourIds && !flavourIds.some((id) => id === flavourId)) {
        return;
      }
      count += quantity * price;
    });
    return count;
  };

  const addToBasket = () => {
    if (flavoursToAdd.length > 0) {
      flavoursToAdd.forEach(({ flavourId, quantity }) => {
        mutate(
          { flavourId, quantity },
          {
            onSuccess: () => {
              fetchUser();
              dispatch({
                type: 'CLEAR',
              });
              setTimeout(() => resetMutation(), 2500);
            },
          }
        );
      });
    }
  };

  const clearFlavoursToAdd = () => {
    dispatch({
      type: 'CLEAR',
    });
  };

  return (
    <ProductListContext.Provider
      value={{
        clearFlavoursToAdd,
        flavoursToAdd,
        addFlavour,
        removeFlavour,
        getUnits,
        getTotal,
        addToBasket,
        isPending,
        isSuccess,
      }}
    >
      {children}
    </ProductListContext.Provider>
  );
}

export function useProductList() {
  const context = useContext(ProductListContext);
  if (!context) {
    throw new Error('useProductList must be used within ProductListProvider');
  }
  return context;
}
